import { Monitor, Moon, Sun } from 'lucide-react';

import { ToggleGroup, ToggleGroupItem } from '@/_shared/components/ui/toggle-group';
import { type Theme, useTheme } from '@/_shared/hooks/useTheme';

const OPTIONS: { value: Theme; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'dark', label: 'Dark', Icon: Moon },
];

// Arrow/Home/End are how the ToggleGroup moves between segments; stop them from
// bubbling so an enclosing menu (the sidebar user dropdown) doesn't also consume them.
const NAV_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

/**
 * ThemeToggle — a light / system / dark segmented control wired to `useTheme`.
 * Reusable anywhere (sidebar user menu today; a Settings → Appearance row later).
 */
export function ThemeToggle({ className }: { className?: string }): React.JSX.Element {
  const { theme, setTheme } = useTheme();

  return (
    <ToggleGroup
      type="single"
      value={theme}
      onValueChange={(value) => {
        // Radix emits '' when the active item is re-pressed; keep the current theme.
        if (value) setTheme(value as Theme);
      }}
      // Let the group own arrow-key navigation without an enclosing menu also consuming it.
      onKeyDown={(e) => {
        if (NAV_KEYS.has(e.key)) e.stopPropagation();
      }}
      variant="outline"
      aria-label="Theme"
      className={className}
    >
      {OPTIONS.map(({ value, label, Icon }) => (
        <ToggleGroupItem key={value} value={value} aria-label={label} title={label}>
          <Icon className="size-4" aria-hidden />
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
